import * as THREE from 'three';
import type { SwordFrame } from '../sword/tracking';
import {
  DRILL_TYPE,
  TARGETS_RING_OUTER_RADIUS_M,
  TECHNIQUE,
  THRUST_ALIGN_MIN_COS,
  type Zone,
} from '../config/constants';
import { sweepBladeAgainst, sphereCollider, type BladePose } from '../hit-detection/sweep';
import type { RuntimeSettings } from './drillRuntime';
import { formatClock } from './timer';
import { createTargetMesh } from '../targets/mesh';
import {
  applyThrust,
  classifyThrust,
  comboMultiplier,
  initialComboState,
  type ArcadeComboState,
} from './arcadeScoring';
import { aggregateArcade, type ArcadeShot } from './arcadeStats';
import { recordArcadeScore } from './arcadeBest';
import type { ArcadeStats } from './stats';
import { t } from '../i18n';
import {
  TargetDrillRuntime,
  type AliveTarget,
  type Resolution,
  type SpawnedTarget,
  type TargetDrillOptions,
} from './targetDrillRuntime';

export type ArcadeThrustRuntimeOptions = TargetDrillOptions & {
  /** Minimum tip speed (m/s) for a thrust to count — a validity gate, never a multiplier. */
  minSpeedMPerSec: number;
  /** Tip gate: fraction of the blade near the punta that counts as a thrust (higher = stricter). */
  tipMinT: number;
};

/**
 * The arcade thrust ("punte") run: the mid-air thrust drill turned into a score attack. A
 * thrust resolves a target by centering (clean = near the center, sloppy = out by the rim);
 * speed stays a validity gate. Scoring + combo are shared with the cut run; the phase machine
 * lives in the base. Free (no-timer) runs show the live score but never persist or record a best.
 */
export class ArcadeThrustRuntime extends TargetDrillRuntime<null> {
  override readonly drillType = DRILL_TYPE.arcade;

  private shots: ArcadeShot[] = [];
  private comboState: ArcadeComboState = initialComboState;
  private minSpeedMPerSec: number;
  private tipMinT: number;

  constructor(opts: ArcadeThrustRuntimeOptions) {
    super(opts);
    this.minSpeedMPerSec = opts.minSpeedMPerSec;
    this.tipMinT = opts.tipMinT;
  }

  applySettings(settings: Partial<RuntimeSettings>): void {
    if (settings.targetsSizeFactor !== undefined) this.setSizeFactor(settings.targetsSizeFactor);
    if (settings.targetsMinSpeed !== undefined) this.minSpeedMPerSec = settings.targetsMinSpeed;
    if (settings.targetsDistanceM !== undefined) this.distanceM = settings.targetsDistanceM;
    if (settings.targetsTtlMs !== undefined) this.ttlMs = settings.targetsTtlMs;
    if (settings.targetsSpawnDelayMs !== undefined)
      this.spawnDelayMs = settings.targetsSpawnDelayMs;
    if (settings.targetsTipMinT !== undefined) this.tipMinT = settings.targetsTipMinT;
  }

  protected resetRun(): void {
    this.shots = [];
    this.comboState = initialComboState;
  }

  protected spawnTarget(center: THREE.Vector3, zone: Zone): SpawnedTarget<null> {
    void center;
    void zone;
    const group = createTargetMesh();
    const ringMat = group.userData['ringMat'] as THREE.MeshBasicMaterial;
    const dotMat = group.userData['dotMat'] as THREE.MeshBasicMaterial;
    const discMat = group.userData['discMat'] as THREE.MeshBasicMaterial;
    return {
      group,
      fadeMats: [ringMat, dotMat, discMat],
      missColorMats: [ringMat, dotMat],
      data: null,
    };
  }

  protected resolveFrame(
    now: number,
    frame: SwordFrame,
    prevBlade: BladePose | null,
    alive: AliveTarget<null>,
  ): Resolution | null {
    const cur: BladePose = { base: frame.basePos, tip: frame.tipPos };
    const speed = frame.tipVelocity.length();
    // Forward thrust = tip advancing along +bladeAxis (tip leading). A pull-back moves along
    // −bladeAxis, so the signed cosine is negative → rejected. bladeAxis is already unit.
    const forwardCos = speed > 1e-6 ? frame.tipVelocity.dot(frame.bladeAxis) / speed : 0;
    if (speed < this.minSpeedMPerSec || forwardCos < THRUST_ALIGN_MIN_COS) return null;

    const ringRadius = TARGETS_RING_OUTER_RADIUS_M * this.sizeFactor;
    const collider = sphereCollider(alive.center, ringRadius);
    // Full-blade sweep prev→cur so a fast punta is caught even if the tip tunnels through the
    // small ring; gate on tAlongSegment so only a tip contact counts.
    const hit = sweepBladeAgainst(prevBlade, cur, collider);
    if (!hit || hit.tAlongSegment < this.tipMinT) return null;

    const accuracyM = hit.local?.['distanceFromCenter'] ?? 0;
    return this.registerHit(now, alive, speed, accuracyM, ringRadius);
  }

  private registerHit(
    now: number,
    alive: AliveTarget<null>,
    speed: number,
    accuracyM: number,
    ringRadius: number,
  ): Resolution {
    const outcome = classifyThrust(accuracyM, ringRadius);
    const { state, pointsGained } = applyThrust(this.comboState, outcome);
    this.comboState = state;
    this.shots.push({
      tMs: now,
      outcome,
      pointsGained,
      comboAfter: state.combo,
      accuracyM,
      reactionMs: now - alive.spawnedAtMs,
      zone: alive.zone,
    });

    // Thrust marker at the target center (billboards toward the user), plus haptic + audio.
    this.feedback.fire({ kind: 'thrust', point: alive.center.clone(), speed });
    return { hit: true };
  }

  protected resolveTimeout(now: number, alive: AliveTarget<null>): void {
    const { state } = applyThrust(this.comboState, 'timeout');
    this.comboState = state;
    this.shots.push({
      tMs: now,
      outcome: 'timeout',
      pointsGained: 0,
      comboAfter: state.combo,
      zone: alive.zone,
    });
  }

  protected aggregateStats(): ArcadeStats {
    return aggregateArcade(this.shots, TECHNIQUE.thrust, this.difficulty);
  }

  protected override onSessionPersisted(): void {
    recordArcadeScore(this.comboState.score, TECHNIQUE.thrust, this.difficulty);
  }

  protected renderHud(nowMs: number): void {
    const hits = this.shots.filter((s) => s.outcome === 'clean' || s.outcome === 'sloppy').length;
    this.hud.render({
      // Free mode has no countdown — show the hit count in the timer slot, score below.
      timerLabel: this.isFree ? t('freeHits')(hits) : formatClock(this.timer.remainingMs(nowMs)),
      lastShot: null,
      arcade: {
        score: this.comboState.score,
        combo: this.comboState.combo,
        multiplier: comboMultiplier(this.comboState.combo),
      },
    });
  }
}
